import { crmFicheHref, crmFicheOf } from '../../../supabase/functions/_shared/crmFiche';
import { targetFromLocation } from '../workQueue/cockpitView';
import type { PeekKind } from './entityPeek';

/**
 * « Ouvrir dans le CRM » from the Bureau, and the way back (mock-up « Fiche » : « Retour à la
 * mission »). The CRM page carries where it was opened from in its URL (`retour`, principle 5); the
 * layout shows the way back as long as that parameter is there. Only a path of the Bureau or of the
 * Équipe is accepted: the parameter never sends anyone off the app. The CRM page of an object is
 * its fiche, defined once for the admin and Otto (`_shared/crmFiche.ts`). Pure.
 */

export const RETURN_PARAM = 'retour';

/** The CRM fiche of an object, remembering the Bureau page it was opened from. */
export function crmHref(kind: PeekKind, id: string, returnTo?: string | null): string {
  const [path, query = ''] = crmFicheHref(kind, id).split('?');
  const params = new URLSearchParams(query);
  const back = safeReturn(returnTo ?? null);
  if (back) params.set(RETURN_PARAM, back);
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

const decoded = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/**
 * The return path when it is a page of the Bureau or of the Équipe, else null. A dot segment,
 * raw or encoded, is refused: the browser resolves `/admin/travail/../../login` out of the Bureau.
 */
export function safeReturn(value: string | null): string | null {
  if (!value || !/^\/admin\/(travail|equipe)(?=$|[/?#])[^\s\\]*$/u.test(value) || value.includes('//')) return null;
  const path = value.split(/[?#]/u)[0];
  if (path.split('/').some((segment) => ['.', '..'].includes(decoded(segment)))) return null;
  return value;
}

/** What the way back says: the mission open there, the Équipe, or the Bureau (any other object). */
export function returnLabel(path: string): string {
  const [pathname, query = ''] = path.split('?');
  if (pathname.startsWith('/admin/equipe')) return "Retour à l'Équipe";
  const [, , , source, sourceId] = pathname.split('/');
  const target = targetFromLocation({ source, sourceId }, new URLSearchParams(query));
  return target?.kind === 'item' || target?.kind === 'mission' ? 'Retour à la mission' : 'Retour au Bureau';
}

/**
 * The CRM object a link of Otto's prose points to — the fiche his tools return (`entity_urls.ts`)
 * or a form he wrote before it — so the name he wrote shows its peek: an account, a contact or an
 * opportunity, by its id.
 */
export function crmTargetOf(href: string): { kind: PeekKind; id: string } | null {
  return crmFicheOf(href);
}
